// hooks/useSearch.js
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import API from "../../../app/api";
import { fetchSearchResults, clearSearchResults } from "../redux/searchSlice";
import { useHeader } from "../../../app/CartWishlistContext";

export const useSearch = () => {
  const dispatch = useDispatch();
  const { setShowSearchIcon } = useHeader();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const searchSectionRef = useRef(null);
  const debounceRef = useRef(null);
  const observerRef = useRef(null);

  const { results: reduxResults = [], loading: reduxLoading = false } =
    useSelector((state) => state.search || {});

  const [apiResults, setApiResults] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);

  const results = reduxResults.length > 0 ? reduxResults : apiResults;
  const loading = reduxResults.length > 0 ? reduxLoading : apiLoading;

  /* ---------------- INTERSECTION OBSERVER ---------------- */
  useEffect(() => {
    if (!searchSectionRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (!isSearchActive) {
          setShowSearchIcon(!entry.isIntersecting);
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(searchSectionRef.current);

    return () => observerRef.current?.disconnect();
  }, [setShowSearchIcon, isSearchActive]);

  /* ---------------- DEBOUNCED SEARCH ---------------- */
  useEffect(() => {
    if (!query.trim()) {
      dispatch(clearSearchResults());
      setApiResults([]);
      return;
    }

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      // Dispatch Redux search
      dispatch(fetchSearchResults({ query: query.trim() }));

      // Fallback API search
      try {
        setApiLoading(true);
        const response = await API.get(
          `/search?q=${encodeURIComponent(query.trim())}`
        );
        const products = response?.data?.data?.products || [];
        setApiResults(products);
      } catch (error) {
        console.error("Search API Error:", error);
        setApiResults([]);
      } finally {
        setApiLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [query, dispatch]);

  /* ---------------- BODY SCROLL LOCK ---------------- */
  useEffect(() => {
    let scrollY = 0;
    if (isSearchActive) {
      scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    }
  }, [isSearchActive]);

  /* ---------------- OUTSIDE CLICK ---------------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchSectionRef.current &&
        !searchSectionRef.current.contains(event.target)
      ) {
        closeSearch();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* ---------------- HANDLERS ---------------- */
  const handleFocus = () => {
    setIsSearchActive(true);
    setShowSearchIcon(false);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const closeSearch = () => {
    setIsSearchActive(false);
    setQuery("");
    dispatch(clearSearchResults());
    setApiResults([]);
    setShowSearchIcon(true);
  };

  return {
    searchSectionRef,
    inputRef,
    isSearchActive,
    setIsSearchActive,
    query,
    setQuery,
    results,
    loading,
    handleFocus,
    closeSearch,
  };
};